import { Button, Grid, ScrollView, SearchField } from "@aws-amplify/ui-react";
import { useMemo, useState } from "react";
import LocalizedStrings from "react-localization";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import ConfirmModal from "components/utils/ConfirmModal";
import ActionIcon from "components/actions/ActionIcon";
import demoExpensesAndInvestments from "config/demo/expensesAndInvestments.json";
import ExpensesAndInvestmentForm from "components/agro/expensesAndInvestments/ExpensesAndInvestmentForm";

const ExpensesAndInvestments = () => {
  const [items] = useState(
    demoExpensesAndInvestments && demoExpensesAndInvestments.slice
      ? demoExpensesAndInvestments.slice(0, 10)
      : []
  );
  const [itemDetails, setItemDetails] = useState();
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    itemId: null,
    itemName: null,
  });
  const [modalForm, setModalForm] = useState({
    visible: false,
    itemId: null,
    action: null,
  });

  const columnsMapped = useMemo(
    () =>
      items.map((it) => ({
        ...it,
        actions: (
          <>
            <ActionIcon
              name={
                itemDetails?.id === it.id
                  ? strings.expenses.hide
                  : strings.expenses.show
              }
              icon={itemDetails?.id === it.id ? "VisibilityOff" : "Visibility"}
              color="primary"
              onClick={() => {
                itemDetails?.id === it.id
                  ? setItemDetails(null)
                  : setItemDetails(it);
              }}
            />
            <ActionIcon
              name={strings.expenses.edit}
              icon="Edit"
              color="primary"
              onClick={() => openModal("update", it.id)}
            />
            <ActionIcon
              name={strings.expenses.delete}
              icon="Delete"
              color="red.60"
              onClick={() =>
                setModalDetails({
                  visible: true,
                  itemId: it.id,
                  itemName: `${it.expenseType || it.id}`,
                })
              }
            />
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, itemDetails?.id]
  );

  const openModal = (action, id = null) =>
    setModalForm({ visible: true, action, itemId: id });

  const tableColumns = [
    {
      key: "date",
      label: strings.table.headers.date,
      _style: { width: "20%" },
    },
    {
      key: "expenseType",
      label: strings.table.headers.expenseType,
      _style: { width: "25%" },
    },
    {
      key: "amount",
      label: strings.table.headers.amount,
      _style: { width: "20%" },
    },
    { key: "actions", label: "", _style: { width: "15%" } },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      <Button
        style={styles.buttonAdd}
        children={strings.expenses.add}
        variation="primary"
        onClick={() => openModal("create")}
      />
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
          />
        </Grid>

        <CTable
          className="table-centered"
          items={columnsMapped}
          columns={tableColumns}
          tableHeadProps={{ color: "light" }}
          responsive
          hover
        />

        <CCollapse visible={!!itemDetails?.id} style={styles.collapseCard}>
          <CCard key={itemDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "
                {itemDetails?.expenseType || itemDetails?.id}"
              </h4>

              <p className="text-muted">
                <b>{strings.details.date}:</b>{" "}
                {itemDetails?.date
                  ? moment(itemDetails.date).format("YYYY-MM-DD")
                  : "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.expenseType}:</b>{" "}
                {itemDetails?.expenseType ?? "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.description}:</b>{" "}
                {itemDetails?.description ?? "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.amount}:</b> {itemDetails?.amount ?? "-"}
              </p>

              <p className="text-muted">
                <b>{strings.details.supplierName}:</b>{" "}
                {itemDetails?.supplierData?.name ?? "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.supplierTaxId}:</b>{" "}
                {itemDetails?.supplierData?.taxId ?? "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.supplierInvoice}:</b>{" "}
                {itemDetails?.supplierData?.invoice ?? "-"}
              </p>

              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {itemDetails?.createdAt
                  ? moment(itemDetails.createdAt).format("YYYY-MM-DD HH:mm:ss")
                  : "-"}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>

      <ConfirmModal
        visible={modalDetails.visible}
        body={strings.formatString(strings.modal.confirmDeleteQuestion, {
          name: modalDetails.itemName,
        })}
        onCancel={() => setModalDetails({ visible: false, itemId: null })}
      />

      {modalForm.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalForm.visible}
          onClose={() => setModalForm({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalForm.action === "create"
                ? strings.modal.addTitle
                : strings.modal.updateTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              <ExpensesAndInvestmentForm
                type={modalForm.action}
                initialValues={
                  modalForm.action === "update"
                    ? items.find((i) => i.id === modalForm.itemId) || {}
                    : {}
                }
                onSubmit={() => {}}
                onCancel={() => setModalForm({ visible: false })}
              />
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Gastos e Inversiones",
    expenses: {
      add: "Registrar Gasto/Inversión",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    table: {
      headers: {
        date: "Fecha",
        expenseType: "Tipo",
        amount: "Valor",
        supplier: "Proveedor",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles Gasto/Inversión",
      date: "Fecha",
      expenseType: "Tipo de gasto/inversión",
      description: "Descripción",
      amount: "Valor",
      supplierName: "Proveedor (Nombre)",
      supplierTaxId: "Proveedor (NIT)",
      supplierInvoice: "Factura compra",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar el registro {name}?",
      addTitle: "Registrar Gasto o Inversión",
      updateTitle: "Actualizar Gasto o Inversión",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  collapseCard: { marginTop: "15px" },
};

export default ExpensesAndInvestments;
